import { OverlayContainer } from "@angular/cdk/overlay";
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { ApiService } from "src/app/services/api.service";
import {
  ChimeMeetingService,
  DeviceLists,
  JoinOptions,
  MeetingMessage,
  PresenceUpdate,
  TranscriptUpdate,
} from "src/app/services/chime-meeting.service";
import { MeetingThemeService } from "src/app/services/meeting-theme.service";
import { computeLayout, LayoutMode, LayoutResult, TileRect, tileStyle } from "./meeting-layout";
import {
  avatarColor,
  CHAT_LIFETIME_MS,
  CHAT_MAX_CHARS,
  ChatMessage,
  copyText,
  fitsDataMessage,
  formatDuration,
  initialsOf,
  MeetingLeftEvent,
  MeetingPerson,
  meetingLink,
  newMessageId,
  REACTIONS,
  reactionEmoji,
  ReactionType,
  TOPIC_CHAT,
  TOPIC_CTRL,
  TOPIC_HAND,
  TOPIC_REACT,
  TranscriptLine,
} from "./meeting-utils";
import { redactPhi } from "./phi-redact";

type Panel = "chat" | "people" | "neo" | "transcript" | "effects" | "info";
type View = "gallery" | "speaker" | "focus";

/**
 * The room's line icons (24px grid, drawn with `stroke: currentColor` by the `.ico` rule). The
 * design uses thin outline icons that neither Material icon font has.
 */
const ICONS = {
  chat: ["M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"],
  people: [
    "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2",
    "M5 7a4 4 0 1 0 8 0a4 4 0 1 0-8 0",
    "M22 21v-2a4 4 0 0 0-3-3.87",
    "M16 3.13a4 4 0 0 1 0 7.75",
  ],
  sparkles: [
    "M9.94 15.5A2 2 0 0 0 8.5 14.06l-6.14-1.58a.5.5 0 0 1 0-.96L8.5 9.94A2 2 0 0 0 9.94 8.5l1.58-6.14a.5.5 0 0 1 .96 0l1.58 6.14a2 2 0 0 0 1.44 1.44l6.14 1.58a.5.5 0 0 1 0 .96l-6.14 1.58a2 2 0 0 0-1.44 1.44l-1.58 6.14a.5.5 0 0 1-.96 0z",
    "M20 3v4",
    "M22 5h-4",
    "M3 19a1 1 0 1 0 2 0a1 1 0 1 0-2 0",
  ],
  sparkle: [
    "M9.94 15.5A2 2 0 0 0 8.5 14.06l-6.14-1.58a.5.5 0 0 1 0-.96L8.5 9.94A2 2 0 0 0 9.94 8.5l1.58-6.14a.5.5 0 0 1 .96 0l1.58 6.14a2 2 0 0 0 1.44 1.44l6.14 1.58a.5.5 0 0 1 0 .96l-6.14 1.58a2 2 0 0 0-1.44 1.44l-1.58 6.14a.5.5 0 0 1-.96 0z",
    "M20 20v.01",
  ],
  smile: ["M2 12a10 10 0 1 0 20 0a10 10 0 1 0-20 0", "M8 14s1.5 2 4 2 4-2 4-2", "M9 9h.01", "M15 9h.01"],
  grid: [
    "M4 3h5a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z",
    "M15 3h5a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z",
    "M15 14h5a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1z",
    "M4 14h5a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1z",
  ],
  more: [
    "M4.25 12a.75.75 0 1 0 1.5 0a.75.75 0 1 0-1.5 0",
    "M11.25 12a.75.75 0 1 0 1.5 0a.75.75 0 1 0-1.5 0",
    "M18.25 12a.75.75 0 1 0 1.5 0a.75.75 0 1 0-1.5 0",
  ],
  video: [
    "M16 13l5.22 3.48a.5.5 0 0 0 .78-.42V7.87a.5.5 0 0 0-.75-.43L16 10.5",
    "M4 6h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z",
  ],
  videoOff: [
    "M10.66 6H14a2 2 0 0 1 2 2v2.5l5.25-3.06a.5.5 0 0 1 .75.43v8.2",
    "M16 16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2",
    "M2 2l20 20",
  ],
  mic: ["M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z", "M19 10v2a7 7 0 0 1-14 0v-2", "M12 19v3"],
  micOff: [
    "M2 2l20 20",
    "M18.89 13.23A7.12 7.12 0 0 0 19 12v-2",
    "M5 10v2a7 7 0 0 0 12 5",
    "M15 9.34V5a3 3 0 0 0-5.68-1.33",
    "M9 9v3a3 3 0 0 0 5.12 2.12",
    "M12 19v3",
  ],
  share: ["M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z", "M16 12l-4-4-4 4", "M12 16V8"],
  shareStop: ["M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z", "M15 9l-6 6", "M9 9l6 6"],
  phoneOff: [
    "M10.1 13.9a14 14 0 0 0 3.73 2.67 1 1 0 0 0 1.21-.3l.36-.47A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 7.27 16.73",
    "M22 2L2 22",
    "M4.76 13.58A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.47.35a1 1 0 0 0-.29 1.23c.07.16.15.32.24.47",
  ],
  chevron: ["M6 9l6 6 6-6"],
  chevronLeft: ["M15 18l-6-6 6-6"],
  chevronRight: ["M9 18l6-6-6-6"],
  zoomIn: ["M11 3a8 8 0 1 0 0 16a8 8 0 1 0 0-16z", "M21 21l-4.35-4.35", "M11 8v6", "M8 11h6"],
  zoomOut: ["M11 3a8 8 0 1 0 0 16a8 8 0 1 0 0-16z", "M21 21l-4.35-4.35", "M8 11h6"],
  rotateLeft: ["M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8", "M3 3v5h5"],
  rotateRight: ["M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8", "M21 3v5h-5"],
  exit: ["M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4", "M16 17l5-5-5-5", "M21 12H9"],
  close: ["M18 6L6 18", "M6 6l12 12"],
  shield: [
    "M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z",
    "M9 12l2 2 4-4",
  ],
  hand: [
    "M18 11V6a2 2 0 0 0-4 0",
    "M14 10V4a2 2 0 0 0-4 0v2",
    "M10 10.5V6a2 2 0 0 0-4 0v8",
    "M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15",
  ],
  user: ["M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2", "M8 7a4 4 0 1 0 8 0a4 4 0 1 0-8 0"],
  focus: [
    "M3 7V5a2 2 0 0 1 2-2h2",
    "M17 3h2a2 2 0 0 1 2 2v2",
    "M21 17v2a2 2 0 0 1-2 2h-2",
    "M7 21H5a2 2 0 0 1-2-2v-2",
    "M10 9h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1z",
  ],
  eye: [
    "M2.06 12.35a1 1 0 0 1 0-.7 10.75 10.75 0 0 1 19.88 0 1 1 0 0 1 0 .7 10.75 10.75 0 0 1-19.88 0",
    "M9 12a3 3 0 1 0 6 0a3 3 0 1 0-6 0",
  ],
  eyeOff: [
    "M10.73 5.08a10.74 10.74 0 0 1 11.2 6.57 1 1 0 0 1 0 .7 10.75 10.75 0 0 1-1.44 2.49",
    "M14.08 14.16a3 3 0 0 1-4.24-4.24",
    "M17.48 17.5a10.75 10.75 0 0 1-15.42-5.15 1 1 0 0 1 0-.7 10.75 10.75 0 0 1 4.45-5.14",
    "M2 2l20 20",
  ],
  check: ["M20 6L9 17l-5-5"],
  keyboard: [
    "M4 6h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z",
    "M6 10h.01",
    "M9.33 10h.01",
    "M12.67 10h.01",
    "M16 10h.01",
    "M7 14h10",
  ],
  fullscreen: ["M3 8V3h5", "M16 3h5v5", "M21 16v5h-5", "M8 21H3v-5"],
  fullscreenExit: ["M8 3v5H3", "M21 8h-5V3", "M3 16h5v5", "M16 21v-5h5"],
  transcript: ["M5 5h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z", "M7 10h10", "M7 14h6"],
  captions: [
    "M5 5h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z",
    "M11 10H9.5A1.5 1.5 0 0 0 8 11.5v1A1.5 1.5 0 0 0 9.5 14H11",
    "M17 10h-1.5a1.5 1.5 0 0 0-1.5 1.5v1a1.5 1.5 0 0 0 1.5 1.5H17",
  ],
  settings: [
    "M9 12a3 3 0 1 0 6 0a3 3 0 1 0-6 0",
    "M12 3.5V5",
    "M12 19v1.5",
    "M3.5 12H5",
    "M19 12h1.5",
    "M6 6l1.06 1.06",
    "M16.94 16.94L18 18",
    "M6 18l1.06-1.06",
    "M16.94 7.06L18 6",
  ],
  info: ["M2 12a10 10 0 1 0 20 0a10 10 0 1 0-20 0", "M12 16v-4", "M12 8h.01"],
  link: [
    "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71",
    "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71",
  ],
  pin: [
    "M12 17v5",
    "M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z",
  ],
};

const LIGHT_THEME_CLASS = "meeting-theme-light";
const DARK_THEME_CLASS = "meeting-theme-dark";

interface Toast {
  id: number;
  icon: string;
  title: string;
  text: string;
  open: Panel | null;
}

interface FloatingReaction {
  id: number;
  emoji: string;
  left: number;
}

const CONTENT_ID = "content";
const SPEAKING_VOLUME = 0.1;
const STAGE_SPEAKER_DELAY_MS = 1500;
const COMPACT_WIDTH = 700;
/** The room's size tiers; these must match the media queries in meeting-room.component.scss. */
const DENSE_QUERY = "(max-height: 860px), (max-width: 1366px)";
const TIGHT_QUERY = "(max-height: 720px)";
/** The zoom buttons' steps on someone else's shared screen; the last is the most it can zoom. */
const CONTENT_ZOOM_LEVELS = [1, 1.25, 1.5, 2, 2.5, 3, 4];
const CONTENT_ZOOM_MAX = CONTENT_ZOOM_LEVELS[CONTENT_ZOOM_LEVELS.length - 1];

function clampZoom(zoom: number): number {
  return zoom < 1.01 ? 1 : Math.min(CONTENT_ZOOM_MAX, zoom);
}

@Component({
  selector: "app-meeting-room",
  standalone: false,
  templateUrl: "./meeting-room.component.html",
  styleUrls: ["./meeting-room.component.scss"],
})
export class MeetingRoomComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() meetingResponse: any;
  @Input() attendeeResponse: any;
  @Input() audioOnly = false;
  @Input() title = "Meeting";
  @Input() publicCode = "";
  @Input() transcriptionEnabled = false;
  @Input() transcriptionError = "";
  @Input() startMuted = false;
  @Input() startCameraOn = true;
  @Input() devices: JoinOptions = {};
  @Input() displayName = "";
  @Input() hostEmail = "";
  /** The signed-in user organised this meeting: Leave then offers "End meeting for all". */
  @Input() isHost = false;
  @Output() left = new EventEmitter<MeetingLeftEvent>();
  @ViewChild("audio", { static: true }) audioEl?: ElementRef<HTMLAudioElement>;
  @ViewChild("stage", { static: true }) stageEl?: ElementRef<HTMLElement>;
  @ViewChild("deviceCard") deviceCardEl?: ElementRef<HTMLElement>;

  /** The room is light-only; the colour tokens the panels' child components read hang off this class. */
  @HostBinding("class.meeting-theme-light") readonly lightTheme = true;

  readonly contentId = CONTENT_ID;
  readonly reactions = REACTIONS;
  readonly icons = ICONS;

  people: MeetingPerson[] = [];
  names = new Map<string, string>();
  content: { tileId: number; local: boolean; ownerId: string } | null = null;
  lines: TranscriptLine[] = [];
  partial: { speaker: string; speakerId: string; raw: string; text: string } | null = null;
  messages: ChatMessage[] = [];
  toasts: Toast[] = [];
  floating: FloatingReaction[] = [];

  panel: Panel | null = null;
  /** Set the first time Neo.ai opens; the panel is then kept alive (hidden) so its conversation survives closing it. */
  neoStarted = false;
  /** The floating device-settings card above the toolbar. */
  devicesOpen = false;
  view: View = "gallery";
  pinnedId: string | null = null;
  stageSpeakerId: string | null = null;
  layout: LayoutResult = { rects: {}, overflow: null, pager: null };
  tileStyles: { [id: string]: { [key: string]: string } } = {};
  overflowStyle: { [key: string]: string } = {};
  pagerStyle: { [key: string]: string } = {};
  /** The page of the strip above the large tile; the layout clamps it as people leave. */
  stripPage = 0;
  /** This viewer's zoom and rotation (in degrees, a multiple of 90) of someone else's shared screen. */
  contentZoom = 1;
  contentRotation = 0;
  contentViewStyle: { [key: string]: string } = {};
  contentDragging = false;
  readonly contentZoomMax = CONTENT_ZOOM_MAX;
  private contentPan = { x: 0, y: 0 };
  private contentPointers = new Map<number, { x: number; y: number }>();
  /** Where a drag or pinch started, relative to the tile's centre (`x`, `y`) and to the page (`centreX`, `centreY`). */
  private contentGesture: {
    zoom: number;
    distance: number;
    x: number;
    y: number;
    panX: number;
    panY: number;
    centreX: number;
    centreY: number;
  } | null = null;
  compact = false;

  muted = false;
  cameraOn = true;
  audioOff = false;
  sharing = false;
  handRaisedAt: number | null = null;
  captionsOn = false;
  selfViewHidden = false;
  fullscreen = false;
  canShare = false;
  canChooseSpeaker = false;
  blurSupported = false;
  blur = false;
  deviceLists: DeviceLists = { mics: [], speakers: [], cameras: [] };
  current = { audioInputId: "", audioOutputId: "", videoInputId: "", blur: false };
  localLevel = 0;

  startedAt = 0;
  now = Date.now();
  unreadChat = 0;
  leaving = false;
  endingForAll = false;
  reconnecting = false;
  poorConnection = false;
  transcriptNotice = "";
  shareError = "";
  error = "";

  private saved = new Set<string>();
  private chatIds = new Set<string>();
  private requested = new Set<string>();
  private nameTimer: any = null;
  private clockTimer: any = null;
  private speakerTimer: any = null;
  private handTimer: any = null;
  private speakerCandidate: string | null = null;
  private reactionTimers = new Map<string, any>();
  private timers = new Set<any>();
  private resizeObserver: ResizeObserver | null = null;
  private finished = false;
  /** When this client started joining; chat sent before it is replayed history. */
  private joinedAt = Date.now();
  private lastReactionAt = 0;
  private nextId = 1;

  constructor(
    private chime: ChimeMeetingService,
    private api: ApiService,
    private zone: NgZone,
    private themes: MeetingThemeService,
    private overlay: OverlayContainer
  ) {}

  async ngOnInit() {
    this.useLightOverlay(true);
    this.audioOff = !!this.devices.noAudio;
    this.muted = this.startMuted || this.audioOff;
    this.cameraOn = this.startCameraOn && !this.audioOnly;
    this.blur = !!this.devices.blur;
    this.canShare = !this.audioOnly && this.chime.canShareScreen();
    this.canChooseSpeaker = this.chime.canChooseSpeaker();
    this.compact = window.innerWidth < COMPACT_WIDTH;
    this.chime.blurSupported().then((ok) => this.zone.run(() => (this.blurSupported = ok)));
    this.clockTimer = setInterval(() => (this.now = Date.now()), 1000);

    const audio = this.audioEl?.nativeElement as HTMLAudioElement;
    if (this.audioOff) audio.muted = true;
    const observer = {
      audioVideoDidStartConnecting: (reconnecting: boolean) => this.zone.run(() => (this.reconnecting = reconnecting)),
      audioVideoDidStart: () => this.zone.run(() => {
        this.reconnecting = false;
        if (!this.startedAt) this.startedAt = Date.now();
      }),
      audioVideoDidStop: (status: any) => this.onStopped(status),
      videoTileDidUpdate: (tile: any) => this.onTile(tile),
      videoTileWasRemoved: (tileId: number) => this.removeTile(tileId),
      connectionDidBecomePoor: () => this.zone.run(() => (this.poorConnection = true)),
      connectionDidBecomeGood: () => this.zone.run(() => (this.poorConnection = false)),
      connectionDidSuggestStopVideo: () => this.zone.run(() => {
        if (this.cameraOn) this.toast("signal_cellular_alt", "Your network is slow", "Turning off your camera may improve call quality.");
      }),
      contentShareDidStart: () => this.zone.run(() => {
        this.sharing = true;
        this.shareError = "";
      }),
      contentShareDidStop: () => this.zone.run(() => {
        this.sharing = false;
      }),
    };
    const options: JoinOptions = {
      ...this.devices,
      muted: this.muted,
      cameraOn: this.cameraOn,
      beforeStart: () => this.subscribeRealtime(),
    };
    try {
      // Chime fires callbacks many times a second; running it outside Angular keeps change detection
      // to the moments that matter, and every handler re-enters the zone explicitly.
      const started = await this.zone.runOutsideAngular(() => this.chime.join(
        this.meetingResponse,
        this.attendeeResponse,
        audio,
        observer,
        this.audioOnly,
        (line) => this.onTranscript(line),
        (update) => this.onPresence(update),
        options
      ));
      // Left (or navigated away) while still connecting: the service released everything.
      if (started.cancelled) return;
      this.current = this.chime.currentDevices();
      this.blur = this.current.blur;
      if (!started.audio && !this.audioOff) {
        this.muted = true;
        this.toast("mic_off", "Microphone unavailable", "You can still listen and chat.");
      }
      if (!this.audioOnly && this.cameraOn && !started.video) {
        this.cameraOn = false;
        this.toast("videocam_off", "Camera unavailable", "Others will see your initials instead.");
      }
      this.chime.onDevicesChanged(() => this.zone.run(() => this.loadDevices()));
    } catch {
      this.error = "The meeting could not be started. Check your connection and try again.";
    }
  }

  ngAfterViewInit() {
    const stage = this.stageEl?.nativeElement;
    if (!stage || typeof ResizeObserver === "undefined") return;
    this.zone.runOutsideAngular(() => {
      let lastWidth = 0;
      let lastHeight = 0;
      this.resizeObserver = new ResizeObserver(() => {
        const width = stage.clientWidth;
        const height = stage.clientHeight;
        if (width === lastWidth && height === lastHeight) return;
        lastWidth = width;
        lastHeight = height;
        this.zone.run(() => {
          this.compact = window.innerWidth < COMPACT_WIDTH;
          this.relayout();
        });
      });
      this.resizeObserver.observe(stage);
    });
  }

  async ngOnDestroy() {
    this.leaving = true;
    clearInterval(this.clockTimer);
    clearTimeout(this.nameTimer);
    clearTimeout(this.speakerTimer);
    clearTimeout(this.handTimer);
    this.reactionTimers.forEach((timer) => clearTimeout(timer));
    this.timers.forEach((timer) => clearTimeout(timer));
    this.resizeObserver?.disconnect();
    this.useLightOverlay(false);
    if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
    await this.chime.leave();
  }

  // ---- Derived view state ----

  get elapsed(): string {
    return this.startedAt ? formatDuration(this.now - this.startedAt) : "";
  }

  get remoteCount(): number {
    return this.people.filter((person) => !person.local).length;
  }

  get isAlone(): boolean {
    return !!this.startedAt && !this.remoteCount && !this.content;
  }

  get raisedCount(): number {
    return this.people.filter((person) => person.handRaisedAt != null).length;
  }

  /** The avatars in the header: you first, then up to two others. */
  get stackPeople(): MeetingPerson[] {
    return this.people.slice(0, 3);
  }

  /** The last two lines spoken in the past few seconds. Reads from the end; the transcript grows all meeting. */
  get captions(): TranscriptLine[] {
    const recent: TranscriptLine[] = [];
    for (let i = this.lines.length - 1; i >= 0 && recent.length < 2; i--) {
      if (this.now - this.lines[i].at >= 8000) break;
      recent.unshift(this.lines[i]);
    }
    return recent;
  }

  get panelTitle(): string {
    switch (this.panel) {
      case "chat": return "Chat";
      case "people": return "People";
      case "neo": return "Neo.Ai";
      case "transcript": return "Transcript";
      case "effects": return "Video effects";
      case "info": return "Meeting info";
      default: return "";
    }
  }

  get transcriptMessage(): string {
    if (this.transcriptNotice) return this.transcriptNotice;
    if (!this.transcriptionEnabled) return this.transcriptionError || "Transcript could not be started for this meeting.";
    return "";
  }

  get joinUrl(): string {
    return this.publicCode ? meetingLink(this.publicCode) : "";
  }

  get contentLabel(): string {
    if (!this.content) return "";
    if (this.content.local) return "You're presenting";
    const owner = this.people.find((person) => person.attendeeId === this.content?.ownerId);
    return owner ? `${this.nameOf(owner)} is presenting` : "Shared screen";
  }

  nameOf(person: MeetingPerson): string {
    if (person.local) return this.displayName || "You";
    return this.names.get(person.externalUserId) || "Participant";
  }

  /** The tile nameplate: your name is marked "(You)". */
  plateName(person: MeetingPerson): string {
    if (person.local) return this.displayName ? `${this.displayName} (You)` : "You";
    return this.nameOf(person);
  }

  initials(person: MeetingPerson): string {
    return initialsOf(this.nameOf(person));
  }

  color(person: MeetingPerson) {
    return avatarColor(person.externalUserId || person.attendeeId);
  }

  showVideo(person: MeetingPerson): boolean {
    if (person.tileId == null || person.paused) return false;
    return !person.local || this.cameraOn;
  }

  rect(id: string): TileRect | null {
    return this.layout.rects[id] || null;
  }

  isHidden(id: string): boolean {
    const rect = this.rect(id);
    return !rect || rect.hidden;
  }

  isSmall(id: string): boolean {
    const rect = this.rect(id);
    return !!rect && (rect.w < 240 || rect.h < 180);
  }

  isLarge(id: string): boolean {
    const rect = this.rect(id);
    return !!rect && rect.w >= 560;
  }

  trackPerson(_: number, person: MeetingPerson) {
    return person.attendeeId;
  }

  trackById(_: number, item: { id: number }) {
    return item.id;
  }

  trackLine(_: number, line: TranscriptLine) {
    return line.id;
  }

  // ---- Toolbar actions ----

  togglePanel(panel: Panel) {
    this.panel = this.panel === panel ? null : panel;
    if (this.panel === "chat") this.unreadChat = 0;
    if (this.panel === "neo") this.neoStarted = true;
    if (this.panel === "effects") this.loadDevices();
  }

  openPanel(panel: Panel) {
    if (this.panel !== panel) this.togglePanel(panel);
  }

  openDevices() {
    this.devicesOpen = true;
    this.loadDevices();
  }

  closeDevices() {
    this.devicesOpen = false;
  }

  /** The device card closes on a click anywhere outside it, including the toolbar. */
  @HostListener("document:pointerdown", ["$event"])
  onPointerDown(event: PointerEvent) {
    if (!this.devicesOpen) return;
    const target = event.target as Element | null;
    const card = this.deviceCardEl?.nativeElement;
    if (!target || !card || card.contains(target)) return;
    // A select panel opened from inside the card renders in the CDK overlay, outside it.
    if (target.closest(".cdk-overlay-container, .cdk-overlay-popover")) return;
    this.closeDevices();
  }

  @HostListener("document:keydown.escape", ["$event"])
  onEscape(event: Event) {
    // A select or menu that handled Escape itself has marked the event.
    if (!this.devicesOpen || event.defaultPrevented) return;
    this.closeDevices();
  }

  async mute() {
    if (this.audioOff) {
      this.toast("volume_off", "Audio is off", "You joined without audio. Leave and rejoin with computer audio to speak.");
      return;
    }
    if (!this.chime.hasMic()) {
      this.toast("mic_off", "No microphone", "Connect a microphone and choose it in Device settings.");
      return;
    }
    this.muted = !this.muted;
    await this.chime.toggleMute(this.muted);
  }

  async camera() {
    if (this.audioOnly) return;
    if (!this.cameraOn && !this.chime.hasCamera()) {
      this.toast("videocam_off", "No camera", "Connect a camera and choose it in Device settings.");
      return;
    }
    this.cameraOn = !this.cameraOn;
    const me = this.people.find((person) => person.local);
    if (me) me.paused = !this.cameraOn;
    this.people = [...this.people];
    await this.chime.toggleCamera(this.cameraOn);
    this.bindAll();
  }

  async share() {
    if (!this.canShare) return;
    this.shareError = "";
    if (this.sharing) {
      this.sharing = false;
      try {
        await this.chime.toggleScreenShare(false);
      } catch {
        this.sharing = true;
      }
      return;
    }
    try {
      await this.chime.toggleScreenShare(true);
      this.sharing = true;
    } catch (err: any) {
      this.sharing = false;
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "AbortError") return;
      this.shareError = "Screen sharing could not be started.";
    }
  }

  setView(view: View) {
    this.view = view;
    if (view === "gallery") this.pinnedId = null;
    this.relayout();
  }

  pin(attendeeId: string | null) {
    this.pinnedId = attendeeId;
    if (attendeeId && this.view === "focus") this.view = "speaker";
    this.relayout();
  }

  toggleSelfView() {
    this.selfViewHidden = !this.selfViewHidden;
    this.relayout();
  }

  pageStrip(step: number) {
    this.stripPage += step;
    this.relayout();
  }

  // ---- Viewing someone else's shared screen (only this viewer sees the zoom and rotation) ----

  get contentZoomLabel(): string {
    return Math.round(this.contentZoom * 100) + "%";
  }

  get contentViewChanged(): boolean {
    return this.contentZoom !== 1 || this.contentRotation % 360 !== 0;
  }

  zoomContent(step: 1 | -1) {
    const next =
      step > 0
        ? CONTENT_ZOOM_LEVELS.find((level) => level > this.contentZoom + 0.01)
        : [...CONTENT_ZOOM_LEVELS].reverse().find((level) => level < this.contentZoom - 0.01);
    if (next) this.zoomContentTo(next);
  }

  /** A quarter turn clockwise (1) or anticlockwise (-1). */
  rotateContent(step: 1 | -1) {
    // Never wrapped back to 0, so 270 to 360 turns a quarter forward rather than three back.
    this.contentRotation += 90 * step;
    this.contentPan = { x: 0, y: 0 };
    this.updateContentView();
  }

  resetContentView() {
    this.contentZoom = 1;
    // Back to upright the shorter way round.
    this.contentRotation = Math.round(this.contentRotation / 360) * 360;
    this.contentPan = { x: 0, y: 0 };
    this.updateContentView();
  }

  /** Ctrl or ⌘ with the wheel zooms at the pointer; browsers report a trackpad pinch the same way. */
  onContentWheel(event: WheelEvent) {
    if (!this.content || this.content.local || (!event.ctrlKey && !event.metaKey)) return;
    event.preventDefault();
    this.zoomContentTo(this.contentZoom * Math.pow(1.002, -event.deltaY), this.fromCentre(event));
  }

  /** Double-click zooms to 2x on that spot, or back out to fit. */
  onContentDblClick(event: MouseEvent) {
    if (!this.content || this.content.local || (event.target as Element).closest(".content-bar")) return;
    if (this.contentZoom > 1) this.zoomContentTo(1);
    else this.zoomContentTo(2, this.fromCentre(event));
  }

  /** One pointer drags a zoomed screen around; two pinch to zoom. */
  onContentPointerDown(event: PointerEvent) {
    if (!this.content || this.content.local || event.button !== 0) return;
    if ((event.target as Element).closest(".content-bar")) return;
    const tile = event.currentTarget as HTMLElement;
    tile.setPointerCapture(event.pointerId);
    this.contentPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.startContentGesture(tile);
  }

  onContentPointerMove(event: PointerEvent) {
    const gesture = this.contentGesture;
    if (!gesture || !this.contentPointers.has(event.pointerId)) return;
    this.contentPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const spread = this.pointerSpread();
    const zoom = gesture.distance && spread.distance ? clampZoom((gesture.zoom * spread.distance) / gesture.distance) : gesture.zoom;
    const scale = zoom / gesture.zoom;
    // The spot that was under the pointers stays under them as they move and spread.
    const x = spread.x - gesture.centreX;
    const y = spread.y - gesture.centreY;
    this.contentPan = { x: x - (gesture.x - gesture.panX) * scale, y: y - (gesture.y - gesture.panY) * scale };
    this.contentZoom = zoom;
    this.updateContentView();
  }

  onContentPointerUp(event: PointerEvent) {
    if (!this.contentPointers.delete(event.pointerId)) return;
    // A finger lifted mid-pinch: carry on dragging with the one that is left.
    if (this.contentPointers.size) {
      this.startContentGesture(event.currentTarget as HTMLElement);
    } else {
      this.contentGesture = null;
      this.contentDragging = false;
    }
  }

  toggleCaptions() {
    this.captionsOn = !this.captionsOn;
  }

  async toggleFullscreen() {
    try {
      // The whole document goes full screen so Material menus, which render outside this
      // component in the overlay container, stay visible.
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch {
      this.toast("fullscreen", "Full screen isn't available", "");
    }
  }

  async copyLink() {
    const ok = await copyText(this.joinUrl);
    this.toast(ok ? "link" : "error_outline", ok ? "Meeting link copied" : "Couldn't copy the link", ok ? "" : this.joinUrl);
  }

  leave() {
    if (this.leaving) return;
    this.leaving = true;
    void this.exit("left");
  }

  /** Host only. The lobby then ends the meeting for everyone and emails the AI summary. */
  endForAll() {
    if (this.leaving || !this.isHost) return;
    this.leaving = true;
    this.endingForAll = true;
    void this.exit("ended", true);
  }

  dismissToast(toast: Toast) {
    this.toasts = this.toasts.filter((item) => item.id !== toast.id);
  }

  openToast(toast: Toast) {
    if (toast.open) this.openPanel(toast.open);
    this.dismissToast(toast);
  }

  // ---- Devices ----

  async loadDevices() {
    try {
      this.deviceLists = await this.chime.listDevices();
      this.current = this.chime.currentDevices();
    } catch {
      // Device labels need permission; the lists stay as they were.
    }
  }

  async chooseMic(deviceId: string) {
    try {
      await this.chime.chooseMic(deviceId);
    } catch {
      this.toast("mic_off", "Couldn't switch microphone", "");
    }
    this.current = this.chime.currentDevices();
  }

  async chooseSpeaker(deviceId: string) {
    try {
      await this.chime.chooseSpeaker(deviceId);
    } catch {
      this.toast("volume_off", "Couldn't switch speaker", "");
    }
    this.current = this.chime.currentDevices();
  }

  async chooseCamera(deviceId: string) {
    try {
      await this.chime.chooseCamera(deviceId);
    } catch {
      this.toast("videocam_off", "Couldn't switch camera", "");
    }
    this.current = this.chime.currentDevices();
  }

  async setBlur(on: boolean) {
    this.blur = on;
    try {
      await this.chime.setBlur(on);
    } catch {
      this.blur = false;
      this.toast("blur_off", "Background blur isn't available", "");
    }
    this.current = this.chime.currentDevices();
  }

  // ---- Chat, hands, reactions and remote mute over data messages ----

  sendChat(text: string) {
    const payload = { id: newMessageId(), text: text.slice(0, CHAT_MAX_CHARS) };
    if (!fitsDataMessage(payload)) {
      this.toast("error_outline", "Message is too long", "Shorten it and try again.");
      return;
    }
    if (!this.chime.sendMessage(TOPIC_CHAT, payload, CHAT_LIFETIME_MS)) return;
    this.addChat({
      id: payload.id,
      senderId: this.chime.localExternalUserId(),
      sender: this.displayName || "You",
      local: true,
      text: payload.text,
      at: Date.now(),
    });
  }

  toggleHand() {
    this.handRaisedAt = this.handRaisedAt ? null : Date.now();
    const me = this.people.find((person) => person.local);
    if (me) me.handRaisedAt = this.handRaisedAt;
    this.people = [...this.people];
    this.chime.sendMessage(TOPIC_HAND, { raised: !!this.handRaisedAt, at: this.handRaisedAt || 0 });
  }

  react(type: ReactionType) {
    const nowMs = Date.now();
    if (nowMs - this.lastReactionAt < 400) return;
    this.lastReactionAt = nowMs;
    this.chime.sendMessage(TOPIC_REACT, { type });
    const me = this.people.find((person) => person.local);
    this.showReaction(me || null, type);
  }

  muteParticipant(attendeeId: string) {
    this.chime.sendMessage(TOPIC_CTRL, { action: "mute", target: attendeeId });
    const person = this.people.find((item) => item.attendeeId === attendeeId);
    if (person) this.toast("mic_off", `${this.nameOf(person)} was muted`, "");
  }

  muteAll() {
    this.chime.sendMessage(TOPIC_CTRL, { action: "mute" });
    this.toast("mic_off", "Everyone else was muted", "They can unmute themselves.");
  }

  @HostListener("document:keydown", ["$event"])
  onShortcut(event: KeyboardEvent) {
    if (!(event.ctrlKey || event.metaKey) || !event.shiftKey || this.leaving) return;
    const actions: { [key: string]: () => void } = {
      m: () => void this.mute(),
      o: () => void this.camera(),
      k: () => this.toggleHand(),
      e: () => void this.share(),
      h: () => this.leave(),
    };
    const action = actions[event.key.toLowerCase()];
    if (!action) return;
    event.preventDefault();
    action();
  }

  @HostListener("document:fullscreenchange")
  onFullscreenChange() {
    this.fullscreen = !!document.fullscreenElement;
  }

  // ---- Chime events ----

  private subscribeRealtime() {
    this.chime.onMessage(TOPIC_CHAT, (message) => this.zone.run(() => this.receiveChat(message)));
    this.chime.onMessage(TOPIC_HAND, (message) => this.zone.run(() => this.receiveHand(message)));
    this.chime.onMessage(TOPIC_REACT, (message) => this.zone.run(() => this.receiveReaction(message)));
    this.chime.onMessage(TOPIC_CTRL, (message) => this.zone.run(() => this.receiveControl(message)));
    this.chime.onActiveSpeaker((attendeeIds) => this.onActiveSpeakers(attendeeIds));
    this.chime.onLocalMuteChanged((muted) => this.zone.run(() => {
      this.muted = muted;
      const me = this.people.find((person) => person.local);
      if (me) me.muted = muted;
    }));
  }

  private receiveChat(message: MeetingMessage) {
    const data = message.data || {};
    const text = typeof data.text === "string" ? data.text.slice(0, CHAT_MAX_CHARS) : "";
    const id = typeof data.id === "string" ? data.id : "";
    if (!text || !id) return;
    const local = !!message.senderExternalUserId && message.senderExternalUserId === this.chime.localExternalUserId();
    this.addChat({
      id,
      senderId: message.senderExternalUserId,
      sender: local ? this.displayName || "You" : this.senderName(message.senderExternalUserId),
      local,
      text,
      at: message.timestampMs || Date.now(),
    });
    if (local || this.panel === "chat") return;
    // Chime replays the last few minutes of chat to people who join late: history, not news.
    if (message.timestampMs && message.timestampMs < this.joinedAt) return;
    this.unreadChat++;
    this.toast("chat", this.senderName(message.senderExternalUserId), text, "chat");
  }

  private addChat(message: ChatMessage) {
    if (this.chatIds.has(message.id)) return;
    this.chatIds.add(message.id);
    this.messages = [...this.messages, message].sort((a, b) => a.at - b.at);
  }

  private receiveHand(message: MeetingMessage) {
    const person = this.people.find((item) => item.attendeeId === message.senderAttendeeId);
    if (!person) return;
    const raised = !!message.data?.raised;
    const wasRaised = person.handRaisedAt != null;
    person.handRaisedAt = raised ? Number(message.data?.at) || message.timestampMs : null;
    this.people = [...this.people];
    if (raised && !wasRaised) this.toast("back_hand", `${this.nameOf(person)} raised their hand`, "", "people");
  }

  private receiveReaction(message: MeetingMessage) {
    const type = message.data?.type;
    if (!reactionEmoji(type)) return;
    const person = this.people.find((item) => item.attendeeId === message.senderAttendeeId) || null;
    this.showReaction(person, type);
  }

  private async receiveControl(message: MeetingMessage) {
    const data = message.data || {};
    if (data.action !== "mute") return;
    if (data.target && data.target !== this.chime.localAttendeeId()) {
      const person = this.people.find((item) => item.attendeeId === data.target);
      if (person) person.muted = true;
      return;
    }
    if (this.muted || this.audioOff) return;
    this.muted = true;
    await this.chime.toggleMute(true);
    this.toast("mic_off", `${this.senderName(message.senderExternalUserId)} muted you`, "Unmute when you're ready to speak.");
  }

  private showReaction(person: MeetingPerson | null, type: string) {
    const emoji = reactionEmoji(type);
    if (!emoji) return;
    const floatId = this.nextId++;
    this.floating = [...this.floating, { id: floatId, emoji, left: 8 + Math.random() * 30 }];
    this.later(() => (this.floating = this.floating.filter((item) => item.id !== floatId)), 3200);
    if (!person) return;
    person.reaction = emoji;
    clearTimeout(this.reactionTimers.get(person.attendeeId));
    this.reactionTimers.set(person.attendeeId, setTimeout(() => {
      person.reaction = null;
      this.reactionTimers.delete(person.attendeeId);
    }, 3000));
  }

  private onActiveSpeakers(attendeeIds: string[]) {
    const localId = this.chime.localAttendeeId();
    const top = attendeeIds.find((id) => id !== localId && this.people.some((person) => person.attendeeId === id)) || null;
    if (!top || top === this.stageSpeakerId || top === this.speakerCandidate) return;
    this.speakerCandidate = top;
    clearTimeout(this.speakerTimer);
    // Teams waits a moment before switching the stage, so brief interjections don't cause flicker.
    this.speakerTimer = setTimeout(() => {
      const candidate = this.speakerCandidate;
      this.speakerCandidate = null;
      if (!candidate || candidate === this.stageSpeakerId) return;
      this.zone.run(() => {
        this.stageSpeakerId = candidate;
        if (this.view === "speaker" && !this.pinnedId && !this.content) this.relayout();
      });
    }, STAGE_SPEAKER_DELAY_MS);
  }

  private onVolume(attendeeId: string, volume: number | null, muted: boolean | null, signal: number | null) {
    const person = this.people.find((item) => item.attendeeId === attendeeId);
    if (!person) return;
    let changed = false;
    if (muted !== null && person.muted !== muted) {
      person.muted = muted;
      changed = true;
    }
    if (signal !== null && person.signal !== signal) {
      person.signal = signal;
      changed = true;
    }
    if (volume !== null) {
      const speaking = !person.muted && volume > SPEAKING_VOLUME;
      if (person.speaking !== speaking) {
        person.speaking = speaking;
        changed = true;
      }
      if (person.local && this.devicesOpen) {
        this.localLevel = person.muted ? 0 : volume;
        changed = true;
      }
    }
    // Volume updates arrive constantly; only re-render when something visible flipped.
    if (changed) this.zone.run(() => undefined);
  }

  private onStopped(status: any) {
    if (this.leaving) return;
    const reason = this.chime.stopReason(status);
    this.zone.run(() => {
      this.leaving = true;
      void this.exit(reason);
    });
  }

  private async exit(reason: MeetingLeftEvent["reason"], endedForAll = false) {
    await this.chime.leave();
    if (this.finished) return;
    this.finished = true;
    this.left.emit({ reason, durationMs: this.startedAt ? Date.now() - this.startedAt : 0, endedForAll });
  }

  private onTranscript(line: TranscriptUpdate) {
    if (line.failed) {
      this.zone.run(() => {
        this.transcriptNotice = line.statusMessage || "Transcript stopped.";
      });
      return;
    }
    const speaker = this.speakerName(line.speakerId);
    // Names, addresses, dates of birth, and the other spoken identifiers are hidden before captions, the transcript panel, or saving.
    const raw = line.text;
    const text = redactPhi(raw);
    this.zone.run(() => {
      if (line.partial) {
        this.partial = { speaker, speakerId: line.speakerId, raw, text };
        return;
      }
      this.partial = null;
      if (!this.lines.some((item) => item.id === line.resultId)) {
        this.lines = [...this.lines, { id: line.resultId, speakerId: line.speakerId, speaker, raw, text, at: Date.now() }];
      }
    });
    if (line.partial || !this.publicCode || this.saved.has(line.resultId)) return;
    this.saved.add(line.resultId);
    this.api.appendMeetingTranscript({
      publicCode: this.publicCode,
      resultId: line.resultId,
      speakerExternalUserId: line.speakerId,
      text,
      startTimeMs: line.startTimeMs,
    }).subscribe({ error: () => undefined });
  }

  private speakerName(externalUserId: string): string {
    if (externalUserId && externalUserId === this.chime.localExternalUserId()) return "You";
    if (externalUserId) {
      const name = this.names.get(externalUserId);
      if (name) return name;
      this.requestNames([externalUserId]);
    }
    return "Speaker";
  }

  private senderName(externalUserId: string): string {
    if (!externalUserId) return "Participant";
    const name = this.names.get(externalUserId);
    if (name) return name;
    this.requestNames([externalUserId]);
    return "Participant";
  }

  private onPresence(update: PresenceUpdate) {
    this.zone.run(() => {
      if (!update.present) {
        this.chime.unwatchVolume(update.attendeeId);
        this.people = this.people.filter((person) => person.attendeeId !== update.attendeeId);
        if (this.pinnedId === update.attendeeId) this.pinnedId = null;
        if (this.stageSpeakerId === update.attendeeId) this.stageSpeakerId = null;
        this.relayout();
        return;
      }
      const isNew = !this.people.some((person) => person.attendeeId === update.attendeeId);
      const person = this.upsertPerson(update.attendeeId, update.externalUserId);
      if (!person.local) {
        this.requestNames([person.externalUserId]);
        if (isNew) this.resendHand();
      }
      this.bindAll();
      this.relayout();
    });
  }

  /** Someone new joined: if our hand is up, tell them, since hand state is not retained. */
  private resendHand() {
    if (!this.handRaisedAt) return;
    clearTimeout(this.handTimer);
    this.handTimer = setTimeout(() => {
      if (this.handRaisedAt) this.chime.sendMessage(TOPIC_HAND, { raised: true, at: this.handRaisedAt });
    }, 1000);
  }

  private onTile(tile: any) {
    if (tile?.tileId == null || !tile.boundAttendeeId) return;
    this.zone.run(() => {
      if (tile.isContent) {
        const ownerId = String(tile.boundAttendeeId).split("#")[0];
        const local = !!tile.localTile || ownerId === this.chime.localAttendeeId();
        if (this.content?.tileId !== tile.tileId) this.forgetContentView();
        this.content = { tileId: tile.tileId, local, ownerId };
      } else {
        const person = this.upsertPerson(tile.boundAttendeeId, tile.boundExternalUserId || "");
        person.tileId = tile.tileId;
        person.local = person.local || !!tile.localTile;
        person.paused = !!tile.paused || (person.local && !tile.active);
        this.people = [...this.people];
        if (!person.local) this.requestNames([person.externalUserId]);
      }
      this.bindAll();
      this.relayout();
    });
  }

  private upsertPerson(attendeeId: string, externalUserId: string): MeetingPerson {
    let person = this.people.find((item) => item.attendeeId === attendeeId);
    if (!person) {
      const local = attendeeId === this.chime.localAttendeeId();
      person = {
        attendeeId,
        externalUserId,
        local,
        tileId: null,
        paused: false,
        muted: local ? this.muted : null,
        speaking: false,
        signal: null,
        handRaisedAt: local ? this.handRaisedAt : null,
        reaction: null,
      };
      this.people = [...this.people, person].sort((a, b) => Number(b.local) - Number(a.local));
      this.chime.watchVolume(attendeeId, (id, volume, muted, signal) => this.onVolume(id, volume, muted, signal));
    } else if (!person.externalUserId && externalUserId) {
      person.externalUserId = externalUserId;
    }
    return person;
  }

  private removeTile(tileId: number) {
    this.zone.run(() => {
      if (this.content?.tileId === tileId) {
        this.content = null;
        this.forgetContentView();
        if (this.view === "focus") this.view = "gallery";
      }
      for (const person of this.people) {
        if (person.tileId === tileId) {
          person.tileId = null;
          person.paused = false;
        }
      }
      this.people = [...this.people];
      this.bindAll();
      this.relayout();
    });
  }

  private bindAll(tries = 0) {
    setTimeout(() => {
      let missing = false;
      const ids = this.people.map((person) => person.tileId).filter((id): id is number => id != null);
      // A presenter's own screen is shown as a placeholder, never as live video.
      if (this.content && !this.content.local) ids.push(this.content.tileId);
      for (const tileId of ids) {
        const element = document.getElementById("tile-" + tileId) as HTMLVideoElement | null;
        if (element) this.chime.bindTile(tileId, element);
        else missing = true;
      }
      if (missing && tries < 10) this.bindAll(tries + 1);
    }, 30);
  }

  private relayout() {
    const stage = this.stageEl?.nativeElement;
    if (!stage) return;
    const local = this.people.find((person) => person.local) || null;
    const remotes = this.people.filter((person) => !person.local);
    let ids = this.people.map((person) => person.attendeeId);
    let mode: LayoutMode = "gallery";
    let mainId: string | null = null;
    if (this.content) {
      mode = "content";
      mainId = CONTENT_ID;
      ids = this.view === "focus" ? [CONTENT_ID] : [CONTENT_ID, ...ids];
    } else if (this.pinnedId && remotes.some((person) => person.attendeeId === this.pinnedId)) {
      mode = "speaker";
      mainId = this.pinnedId;
    } else if (this.view === "speaker" && remotes.length > 1) {
      mode = "speaker";
      const speaker = remotes.find((person) => person.attendeeId === this.stageSpeakerId);
      mainId = (speaker || remotes[0]).attendeeId;
    }
    this.layout = computeLayout({
      width: stage.clientWidth,
      height: stage.clientHeight,
      ids,
      mode,
      mainId,
      selfId: local?.attendeeId || null,
      // In the gallery you are a card like everyone else; beside a large tile you float in the corner.
      selfFloating: mode !== "gallery",
      selfHidden: this.selfViewHidden,
      compact: this.compact,
      scale: this.sizeScale(),
      stripPage: this.stripPage,
    });
    const styles: { [id: string]: { [key: string]: string } } = {};
    for (const id of Object.keys(this.layout.rects)) styles[id] = tileStyle(this.layout.rects[id]);
    this.tileStyles = styles;
    this.overflowStyle = this.layout.overflow ? tileStyle(this.layout.overflow.rect) : {};
    this.stripPage = this.layout.pager?.page || 0;
    this.pagerStyle = this.layout.pager ? tileStyle(this.layout.pager.rect) : {};
    this.updateContentView();
  }

  /**
   * Sizes and moves the box around the shared screen's <video> for this viewer's zoom, pan and
   * rotation. The style is kept even at fit and upright, so the first zoom animates from where the
   * video already is.
   */
  private updateContentView() {
    const rect = this.layout.rects[CONTENT_ID];
    if (!rect || rect.hidden) {
      this.contentViewStyle = {};
      return;
    }
    // A quarter turn swaps the box's width and height, so it still fills the tile once turned.
    const turned = this.contentRotation % 180 !== 0;
    const boxW = turned ? rect.h : rect.w;
    const boxH = turned ? rect.w : rect.h;
    // The picture is letterboxed inside the box; once its size is known, measure the picture itself.
    const video = this.content ? (document.getElementById("tile-" + this.content.tileId) as HTMLVideoElement | null) : null;
    let picW = boxW;
    let picH = boxH;
    if (video?.videoWidth && video.videoHeight) {
      const fit = Math.min(boxW / video.videoWidth, boxH / video.videoHeight);
      picW = video.videoWidth * fit;
      picH = video.videoHeight * fit;
    }
    // A zoomed picture can be dragged until its edge reaches the tile's edge, and no further.
    const maxX = Math.max(0, ((turned ? picH : picW) * this.contentZoom - rect.w) / 2);
    const maxY = Math.max(0, ((turned ? picW : picH) * this.contentZoom - rect.h) / 2);
    const x = Math.min(maxX, Math.max(-maxX, this.contentPan.x));
    const y = Math.min(maxY, Math.max(-maxY, this.contentPan.y));
    this.contentPan = { x, y };
    this.contentViewStyle = {
      left: "50%",
      top: "50%",
      width: `${Math.round(boxW)}px`,
      height: `${Math.round(boxH)}px`,
      transform: `translate(-50%, -50%) translate(${Math.round(x)}px, ${Math.round(y)}px) rotate(${this.contentRotation}deg) scale(${this.contentZoom})`,
    };
  }

  /** A new or ended share starts at fit and upright. */
  private forgetContentView() {
    this.contentZoom = 1;
    this.contentRotation = 0;
    this.contentPan = { x: 0, y: 0 };
    this.contentPointers.clear();
    this.contentGesture = null;
    this.contentDragging = false;
  }

  /** Zooms to `zoom`, keeping the spot at `at` (relative to the tile's centre) where it is. */
  private zoomContentTo(zoom: number, at = { x: 0, y: 0 }) {
    const next = clampZoom(zoom);
    const scale = next / this.contentZoom;
    this.contentPan = { x: at.x - (at.x - this.contentPan.x) * scale, y: at.y - (at.y - this.contentPan.y) * scale };
    this.contentZoom = next;
    this.updateContentView();
  }

  private startContentGesture(tile: HTMLElement) {
    const box = tile.getBoundingClientRect();
    const centreX = box.left + box.width / 2;
    const centreY = box.top + box.height / 2;
    const spread = this.pointerSpread();
    this.contentGesture = {
      zoom: this.contentZoom,
      distance: spread.distance,
      x: spread.x - centreX,
      y: spread.y - centreY,
      panX: this.contentPan.x,
      panY: this.contentPan.y,
      centreX,
      centreY,
    };
    this.contentDragging = true;
  }

  /** The middle of the pointers on the shared screen, and the distance between the first two. */
  private pointerSpread(): { x: number; y: number; distance: number } {
    const points = Array.from(this.contentPointers.values());
    const x = points.reduce((sum, point) => sum + point.x, 0) / points.length;
    const y = points.reduce((sum, point) => sum + point.y, 0) / points.length;
    const distance = points.length > 1 ? Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) : 0;
    return { x, y, distance };
  }

  private fromCentre(event: MouseEvent): { x: number; y: number } {
    const box = (event.currentTarget as HTMLElement).getBoundingClientRect();
    return { x: event.clientX - box.left - box.width / 2, y: event.clientY - box.top - box.height / 2 };
  }

  private requestNames(externalUserIds: string[]) {
    for (const id of externalUserIds) {
      if (id && !this.names.has(id)) this.requested.add(id);
    }
    if (!this.requested.size || !this.publicCode || this.nameTimer) return;
    this.nameTimer = setTimeout(() => {
      this.nameTimer = null;
      const ids = Array.from(this.requested).slice(0, 50);
      ids.forEach((id) => this.requested.delete(id));
      this.api.listMeetingParticipants(this.publicCode, ids).subscribe({
        next: (rows) => this.zone.run(() => {
          for (const row of rows || []) {
            if (row?.externalUserId && row.displayName) this.names.set(row.externalUserId, row.displayName);
          }
          for (const id of ids) {
            if (!this.names.has(id)) this.names.set(id, "Participant");
          }
          this.relabel();
          this.people = [...this.people];
        }),
        error: () => ids.forEach((id) => this.names.set(id, "Participant")),
      });
    }, 250);
  }

  /** Swaps a placeholder speaker label for the person's name once the participant lookup returns. */
  private relabel() {
    if (this.lines.length) {
      this.lines = this.lines.map((line) => {
        const name = line.speakerId ? this.names.get(line.speakerId) : null;
        const speaker = line.speaker === "Speaker" && name ? name : line.speaker;
        return { ...line, speaker, text: redactPhi(line.raw) };
      });
    }
    if (this.partial) this.partial = { ...this.partial, text: redactPhi(this.partial.raw) };
    if (this.messages.length) {
      this.messages = this.messages.map((message) => {
        const name = !message.local && message.senderId ? this.names.get(message.senderId) : null;
        return message.sender === "Participant" && name ? { ...message, sender: name } : message;
      });
    }
  }

  /** The stylesheet's size tier as a factor of the design sizes, for the gaps between tiles. */
  private sizeScale(): number {
    if (window.matchMedia(TIGHT_QUERY).matches) return 0.75;
    if (window.matchMedia(DENSE_QUERY).matches) return 0.85;
    return 1;
  }

  /**
   * Menus render in the CDK overlay container, outside this component, and take their colour
   * tokens from its theme class. The room is light-only, so the container is light while it is
   * open, then goes back to the lobby's theme (a no-op when no meeting screen is attached).
   */
  private useLightOverlay(on: boolean) {
    const classes = this.overlay.getContainerElement().classList;
    if (on) {
      classes.remove(DARK_THEME_CLASS);
      classes.add(LIGHT_THEME_CLASS);
      return;
    }
    classes.remove(LIGHT_THEME_CLASS);
    this.themes.set(this.themes.theme);
  }

  private toast(icon: string, title: string, text: string, open: Panel | null = null) {
    const toast: Toast = { id: this.nextId++, icon, title, text, open };
    this.toasts = [...this.toasts.slice(-2), toast];
    this.later(() => this.dismissToast(toast), 5000);
  }

  private later(fn: () => void, ms: number) {
    const timer = setTimeout(() => {
      this.timers.delete(timer);
      fn();
    }, ms);
    this.timers.add(timer);
  }
}
